import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PublicHeader from "@/components/layout/PublicHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { Brain, Clock, ChevronRight, Sparkles, Check } from "lucide-react";

const CAT_LABEL = {
  NUM_SEQ:    "Number Sequence",
  ANALOGY:    "Verbal Analogy",
  SYLLOGISM:  "Logical Reasoning",
  MATH_LOGIC: "Math Logic",
  ODD_OUT:    "Odd One Out",
  PATTERN:    "Pattern Recognition",
};

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function IqTest() {
  useSEO({
    title: "Maqam IQ Assessment",
    description: "12-question cognitive assessment required for Maqam Premium applicants.",
    path: "/become-premium/iq-test",
  });

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState("intro");  // intro | running | finished
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [language, setLanguage] = useState("en");
  const tickRef = useRef(null);

  const LANGS = [
    { code: "en", label: "English",  flag: "🇬🇧" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "ar", label: "العربية",  flag: "🇸🇦" },
    { code: "de", label: "Deutsch",  flag: "🇩🇪" },
    { code: "it", label: "Italiano", flag: "🇮🇹" },
  ];

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login?next=/become-premium/iq-test");
  }, [user, authLoading, navigate]);

  // Timer
  useEffect(() => {
    if (stage !== "running") return;
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          submitTest();          // auto-submit when time runs out
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const startTest = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/premium/iq-test/start", null, { params: { language } });
      setTest(r.data);
      setAnswers(new Array(r.data.questions.length).fill(-1));
      setRemaining(r.data.duration_minutes * 60);
      setIdx(0);
      setStage("running");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the test.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitTest = async () => {
    if (!test) return;
    setSubmitting(true);
    try {
      const r = await api.post("/premium/iq-test/submit", {
        test_id: test.test_id,
        answers: answers.map((a) => (a < 0 ? -1 : a)),
      });
      setResult(r.data);
      setStage("finished");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const choose = (optIdx) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = optIdx;
      return next;
    });
  };

  const answered = answers.filter((a) => a >= 0).length;
  const q = test?.questions?.[idx];
  const isLast = test && idx === test.questions.length - 1;

  const iqColor = useMemo(() => {
    if (!result) return "text-white";
    if (result.iq >= 130) return "text-[#8b5cf6]";
    if (result.iq >= 115) return "text-[#06b6d4]";
    if (result.iq >= 100) return "text-[#34C759]";
    if (result.iq >= 85)  return "text-[#F4B400]";
    return "text-[#FF453A]";
  }, [result]);

  return (
    <div className="min-h-screen bg-[#0c0c0d] text-white" data-testid="iq-test-page">
      <PublicHeader />

      <section className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
        {stage === "intro" && (
          <div data-testid="iq-intro">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-4">/ MAQAM PREMIUM · STEP 1 OF 2</div>
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-6 flex items-center gap-4">
              <Brain className="w-12 h-12 md:w-14 md:h-14 text-[#8b5cf6]" strokeWidth={1.5} />
              IQ assessment.
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-10">
              Every Premium applicant takes a short Mensa-style cognitive
              assessment. This protects the Premium brand and gives employers
              one extra signal of quality alongside your CV.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: Clock,     label: "15 min",       sub: "time limit" },
                { icon: Brain,     label: "12 questions", sub: "balanced 6 categories" },
                { icon: Sparkles,  label: "70 – 145",     sub: "IQ score range" },
              ].map((s) => (
                <div key={s.label} className="border border-white/10 p-4">
                  <s.icon className="w-5 h-5 text-white/65 mb-3" strokeWidth={1.5} />
                  <div className="font-display font-bold text-2xl tracking-tight">{s.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-0.5">{s.sub}</div>
                </div>
              ))}
            </div>

            <ul className="space-y-3 mb-10 text-sm text-white/80">
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" />6 cognitive areas: number, verbal, syllogisms, math, odd-one-out, pattern</li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" />You can't go back to a previous question — answer carefully</li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" />Result stays valid for 30 days. Take it once, then submit your Premium application</li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" />No prep needed — there's no "right" outside knowledge required</li>
            </ul>

            {/* Language picker */}
            <div className="mb-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/55 mb-3">Choose your test language</div>
              <div className="flex flex-wrap gap-2" data-testid="iq-lang-picker">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLanguage(l.code)}
                    className={`px-4 py-3 border flex items-center gap-2 transition-colors ${
                      language === l.code
                        ? "bg-white text-black border-white"
                        : "border-white/15 text-white/75 hover:border-white/40 hover:text-white"
                    }`}
                    data-testid={`iq-lang-${l.code}`}
                  >
                    <span className="text-xl leading-none">{l.flag}</span>
                    <span className="font-bold text-sm">{l.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={startTest}
              disabled={submitting}
              className="w-full sm:w-auto bg-white text-black hover:bg-white/85 disabled:opacity-50 font-bold px-10 py-5 inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="iq-start-btn"
            >
              {submitting ? "Loading…" : "Start the assessment"}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {stage === "running" && q && (
          <div data-testid="iq-running" dir={test?.language === "ar" ? "rtl" : "ltr"}>
            {/* Top bar — timer + progress */}
            <div className="flex items-center justify-between gap-4 mb-8">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{CAT_LABEL[q.category] || q.category}</div>
                <div className="font-display font-bold text-sm">Question {idx + 1} / {test.questions.length}</div>
              </div>
              <div className={`px-4 py-2 border ${remaining < 60 ? "border-[#FF453A] text-[#FF453A]" : "border-white/20"} flex items-center gap-2 font-mono text-sm tabular-nums`}>
                <Clock className="w-4 h-4" />
                {formatTime(remaining)}
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-1 bg-white/10 mb-12">
              <div
                className="h-full bg-[#8b5cf6] transition-all"
                style={{ width: `${(idx / test.questions.length) * 100}%` }}
              />
            </div>

            <div className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-relaxed mb-10 whitespace-pre-line" data-testid="iq-question">
              {q.q}
            </div>

            <div className="space-y-3 mb-12">
              {q.options.map((opt, i) => {
                const active = answers[idx] === i;
                return (
                  <button
                    key={i}
                    onClick={() => choose(i)}
                    className={`w-full text-left px-5 py-4 border-2 transition-colors font-medium ${
                      active
                        ? "bg-white text-black border-white"
                        : "border-white/15 hover:border-white/40 hover:bg-white/[0.03]"
                    }`}
                    data-testid={`iq-opt-${i}`}
                  >
                    <span className="font-mono text-xs uppercase tracking-widest me-3 opacity-60">
                      {String.fromCharCode(65 + i)}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="font-mono text-xs uppercase tracking-widest text-white/50">
                {answered} / {test.questions.length} answered
              </div>
              {isLast ? (
                <button
                  onClick={submitTest}
                  disabled={submitting || answers[idx] < 0}
                  className="bg-[#34C759] text-black hover:bg-[#34C759]/85 disabled:opacity-50 font-bold px-8 py-3 inline-flex items-center gap-2 transition-colors"
                  data-testid="iq-submit-btn"
                >
                  {submitting ? "Grading…" : "Submit assessment"}
                </button>
              ) : (
                <button
                  onClick={() => setIdx(idx + 1)}
                  disabled={answers[idx] < 0}
                  className="bg-white text-black hover:bg-white/85 disabled:opacity-50 font-bold px-8 py-3 inline-flex items-center gap-2 transition-colors"
                  data-testid="iq-next-btn"
                >
                  Next question <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {stage === "finished" && result && (
          <div data-testid="iq-result" className="text-center">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">/ YOUR RESULT</div>
            <div className="mb-8">
              <div className={`font-display font-black text-[8rem] md:text-[12rem] tracking-tighter leading-none ${iqColor}`}>
                {result.iq}
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/55">
                IQ score · {result.percentile}<sup>{ordinal(result.percentile)}</sup> percentile
              </div>
            </div>
            <div className="text-white/70 mb-10">
              You answered <span className="text-white font-bold">{result.raw} of {result.total}</span> correctly.
              {result.submitted_late && <span className="block mt-2 text-[#FF9500] text-sm">⚠ Submitted after the time limit — counts for review but flagged.</span>}
            </div>

            {/* Category breakdown */}
            <div className="grid sm:grid-cols-3 gap-3 mb-10">
              {Object.entries(result.by_category || {}).map(([cat, v]) => (
                <div key={cat} className="border border-white/10 p-4 text-left">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">{CAT_LABEL[cat] || cat}</div>
                  <div className="font-display font-bold text-2xl">{v.correct} / {v.total}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/become-premium")}
                className="bg-white text-black hover:bg-white/85 font-bold px-8 py-4 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="iq-continue-btn"
              >
                Continue to Premium application <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-white/40 mt-6 max-w-md mx-auto">
              This score is one signal among many. Reviewers also weigh your CV,
              experience, references, and skill badges.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}
